import { saveAs } from "file-saver";
import JSZip from "jszip";
import { map, uniq } from "lodash";
import { callGetDocumentDownloadUrl } from "@documents/store/thunks/callGetDocmentDownloadUrlApi";
import { callGetDownloadDocumentData } from "@documents/store/thunks/callGetDownloadDocumentDataAPI";
import { AppThunkAPI } from "@shared/models/redux";
import { DocumentFile, DocumentRow } from "@documents/models/dataGrid.models";
import { checkDocumentTerms } from "@terms/store/thunks/checkDocumentTerms";
import TermsNotAcceptedError from "@terms/utils/TermsNotAcceptedError";

type Document = Pick<DocumentRow, "id" | "fundId" | "title" | "fileType" | "documentSubtype" | "markedRead">;

type SuccessfullyArchivedDocumentFile = DocumentFile;
type DownloadAndArchiveDocuments = (
  documents: Document[],
  archiveName?: string
) => AppThunkAPI<Promise<SuccessfullyArchivedDocumentFile[]>>;

const BULK_DOWNLOAD_CONCURRENCY = 4;

async function runWithConcurrency<T, R>(
  items: T[],
  worker: (item: T) => Promise<R>,
  concurrency: number
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let nextIndex = 0;

  const runOne = async (): Promise<void> => {
    for (;;) {
      const i = nextIndex++;

      if (i >= items.length) {
        return;
      }

      results[i] = await worker(items[i]);
    }
  };

  const workerCount = Math.min(concurrency, items.length);
  const workers = Array.from({ length: workerCount }, () => runOne());

  await Promise.all(workers);

  return results;
}

export const downloadAndArchiveDocuments: DownloadAndArchiveDocuments =
  (documents, archiveName = "documents") =>
  async (dispatch) => {
    const hasAcceptedTerms = await dispatch(
      checkDocumentTerms({
        documentSubTypes: uniq(map(documents, (document) => String(document.documentSubtype))),
        fundIds: uniq(map(documents, "fundId")),
      })
    );

    if (!hasAcceptedTerms || !hasAcceptedTerms.valueOf) {
      throw new TermsNotAcceptedError();
    }

    const allResults = await runWithConcurrency(
      documents,
      async ({ id, title, fileType }) => {
        const url = await dispatch(callGetDocumentDownloadUrl(id));
        const data = await dispatch(callGetDownloadDocumentData(url));
        const name = title.replace(/[^A-Za-z0-9_ ]/g, "");
        const fileName = `${name}.${fileType}`;
        const content = data instanceof Blob ? data : data.blob;

        return content?.size ? { id, fileName, content } : { id, fileName, content: null };
      },
      BULK_DOWNLOAD_CONCURRENCY
    );

    const documentFiles = allResults.filter((file): file is DocumentFile => file.content !== null);

    const preparedDocumentFiles = documentFiles.reduce((result: Record<string, DocumentFile[]>, file) => {
      return {
        ...result,
        [file.fileName]: [...(result?.[file.fileName] ?? []), file],
      };
    }, {});

    const zip = new JSZip();

    Object.entries(preparedDocumentFiles).forEach(([fileName, files]) => {
      files.forEach(({ content }, index) => {
        const uniqueFileName = index === 0 ? fileName : fileName.split(".").join(`(${index}).`);

        zip.file(uniqueFileName, content);
      });
    });

    saveAs(await zip.generateAsync({ type: "blob" }), archiveName + ".zip");

    return documentFiles;
  };
